// SPDX-License-Identifier: GPL-3.0-or-later
// Decoration registry of the SigmaBots table (B3, client only: it imports three).
// The client passes this map to `createRenderer`. Each factory builds one character group
// or the playfield art plane. Each character is one SigmaBot (see sigmabot/) plus props that show
// the game state. All models are code-built three.js meshes and canvas textures.
//
// Animation protocol (DecorationFactory.animate):
// - `animate(obj, "idle", timeMs)` sets the idle pose. Call it each frame.
// - `animate(obj, "<name>[:<arg>]", timeMs)` plays an event animation, then sets the pose.
//   The names are in CHARACTER_ANIMATIONS (animation.ts).

import {
  CanvasTexture,
  Group,
  Mesh,
  MeshBasicMaterial,
  type Object3D,
  PlaneGeometry,
  SRGBColorSpace,
} from "three";
import type { DecorationFactory } from "../../shared/contracts.ts";
import { parseAnimation } from "../animation.ts";
import { type CreateCanvas, context2d, defaultCreateCanvas } from "../art/canvas.ts";
import { drawPlayfieldArt, PLAYFIELD_HEIGHT, PLAYFIELD_WIDTH } from "../art/playfield.ts";
import { MESH_ID } from "../ids.ts";
import { buildCarl } from "./carl.ts";
import {
  type Built,
  builtOf,
  type CharacterEnv,
  disposeTree,
  mountBuilt,
  orient,
  type Stance,
} from "./common.ts";
import { buildDennis } from "./dennis.ts";
import { buildHaley } from "./haley.ts";
import { buildLarry } from "./larry.ts";
import { buildProd } from "./prod.ts";
import { buildRandy } from "./randy.ts";
import { buildRex } from "./rex.ts";
import { buildTessa } from "./tessa.ts";

export type { Built, CharacterEnv } from "./common.ts";
export { builtOf, disposeTree } from "./common.ts";

/**
 * Pixel width of the playfield art canvas. The height is twice as large. One pixel is one table
 * unit, so the text stays sharp on a phone with a 3x screen.
 */
export const PLAYFIELD_TEXTURE_WIDTH = 1000;

/** Make the environment of the characters. `reducedMotion` is read at each frame. */
export function makeCharacterEnv(
  reducedMotion: () => boolean,
  createCanvas: CreateCanvas = defaultCreateCanvas,
): CharacterEnv {
  return { createCanvas, reducedMotion };
}

/**
 * The playfield art as an object: a plane in the three.js frame (x = table x, z = table y, up = y),
 * just above the playfield. The texture is drawn on a canvas.
 */
export function buildPlayfieldArt(env: CharacterEnv): Object3D {
  const w = PLAYFIELD_TEXTURE_WIDTH;
  const h = w * (PLAYFIELD_HEIGHT / PLAYFIELD_WIDTH);
  const canvas = env.createCanvas(w, h);
  drawPlayfieldArt(context2d(canvas), w, h);
  const texture = new CanvasTexture(canvas as unknown as HTMLCanvasElement);
  texture.colorSpace = SRGBColorSpace;
  const plane = new Mesh(
    new PlaneGeometry(PLAYFIELD_WIDTH, PLAYFIELD_HEIGHT),
    new MeshBasicMaterial({ map: texture }),
  );
  plane.rotation.x = -Math.PI / 2;
  plane.position.set(PLAYFIELD_WIDTH / 2, 0.2, PLAYFIELD_HEIGHT / 2);
  plane.name = "PlayfieldArt";
  const group = new Group();
  group.name = "PlayfieldArtGroup";
  group.add(plane);
  return group;
}

/**
 * How each character stands. The camera sees the table from far away at a steep angle, so a
 * recipe size alone is too small to read. Each character is scaled up and leans back toward
 * the camera, so its face and label can be read at 720 x 900 and at 390 x 844.
 */
export const STANCE: Readonly<Record<string, Stance>> = {
  // The table data turns Dennis 20 degrees to face the playfield. He keeps all of that turn.
  [MESH_ID.dennis]: { scale: 1.5, leanDeg: 38, turn: 1 },
  [MESH_ID.rex]: { scale: 1.25, leanDeg: 35 },
  [MESH_ID.haley]: { scale: 1.4, leanDeg: 38 },
  [MESH_ID.carl]: { scale: 1.6, leanDeg: 38 },
  [MESH_ID.randy]: { scale: 1.8, leanDeg: 35 },
  [MESH_ID.larry]: { scale: 1.5, leanDeg: 38 },
  [MESH_ID.tessa]: { scale: 1.5, leanDeg: 38 },
  [MESH_ID.prod]: { scale: 1.5, leanDeg: 38 },
};

const DEFAULT_STANCE: Stance = { scale: 1.5, leanDeg: 38 };

/** A decoration factory that also frees what it built. The renderer calls `dispose` on teardown. */
interface DisposableDecoration extends DecorationFactory<Object3D> {
  dispose(obj: Object3D): void;
}

function characterFactory(
  id: string,
  build: (env: CharacterEnv) => Built,
  env: CharacterEnv,
): DisposableDecoration {
  return {
    id,
    build: () => mountBuilt(build(env), STANCE[id] ?? DEFAULT_STANCE),
    animate(obj, animation, timeMs) {
      const built = builtOf(obj);
      if (!built) return;
      // The scene sets the yaw of the mount after the build, so the pose is set at each frame.
      orient(obj, STANCE[id] ?? DEFAULT_STANCE);
      const { name, arg } = parseAnimation(animation);
      if (name !== "idle") built.play(name, arg, timeMs);
      built.tick(timeMs);
    },
    dispose: disposeDecoration,
  };
}

/** Build the registry: the eight characters and the playfield art. The map keys are the registry ids. */
export function createDecorationRegistry(
  env: CharacterEnv,
): ReadonlyMap<string, DecorationFactory<Object3D>> {
  const factories: DisposableDecoration[] = [
    characterFactory(MESH_ID.dennis, buildDennis, env),
    characterFactory(MESH_ID.rex, buildRex, env),
    characterFactory(MESH_ID.haley, buildHaley, env),
    characterFactory(MESH_ID.carl, buildCarl, env),
    characterFactory(MESH_ID.randy, buildRandy, env),
    characterFactory(MESH_ID.larry, buildLarry, env),
    characterFactory(MESH_ID.tessa, buildTessa, env),
    characterFactory(MESH_ID.prod, buildProd, env),
    { id: MESH_ID.playfield, build: () => buildPlayfieldArt(env), dispose: disposeDecoration },
  ];
  return new Map(factories.map((f) => [f.id, f]));
}

/** Free a built decoration (geometry, materials, textures). The renderer calls it on dispose. */
export function disposeDecoration(obj: Object3D): void {
  disposeTree(obj);
}
